import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Save, Mail, Cloud, Rss, Plus, Trash2, Globe } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Settings() {
  const [activeTab, setActiveTab] = useState('smtp');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // SMTP Settings
  const [smtp, setSmtp] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    use_tls: true,
    from_email: ''
  });
  
  // Cloud Storage Settings
  const [cloudStorage, setCloudStorage] = useState({
    provider: 's3',
    bucket_name: '',
    access_key: '',
    secret_key: '',
    region: 'us-east-1'
  });
  
  // RSS Feeds
  const [rssFeeds, setRssFeeds] = useState([]);
  const [newFeed, setNewFeed] = useState({
    name: '',
    url: '',
    category: 'generale',
    enabled: true
  });
  
  // Company Domain
  const [companyDomain, setCompanyDomain] = useState('');
  
  // Weather Settings
  const [weather, setWeather] = useState({
    api_key: '',
    location: 'Catanzaro'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        const data = await res.json();
        
        if (data.smtp) {
          setSmtp({
            ...smtp,
            ...data.smtp,
            password: '' // Don't show masked password
          });
        }
        
        if (data.cloud_storage) {
          setCloudStorage({
            ...cloudStorage,
            ...data.cloud_storage,
            secret_key: '', // Don't show masked keys
            connection_string: ''
          });
        }
        
        if (data.rss_feeds) {
          setRssFeeds(data.rss_feeds);
        }
        
        if (data.company_email_domain) {
          setCompanyDomain(data.company_email_domain);
        }
        
        if (data.weather_api_key || data.weather_location) {
          setWeather({
            api_key: '',  // Don't show masked key
            location: data.weather_location || 'Catanzaro'
          });
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setLoading(false);
    }
  };

  const saveSmtpSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/settings/smtp`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(smtp)
      });

      if (!res.ok) throw new Error('Errore salvataggio');

      alert('Impostazioni SMTP salvate con successo');
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveCloudStorageSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/settings/cloud-storage`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cloudStorage)
      });

      if (!res.ok) throw new Error('Errore salvataggio');

      alert('Impostazioni cloud storage salvate con successo');
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addRssFeed = async () => {
    if (!newFeed.name || !newFeed.url) {
      alert('Nome e URL sono obbligatori');
      return;
    }

    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/settings/rss-feeds`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newFeed)
      });

      if (!res.ok) throw new Error('Errore aggiunta feed');

      alert('Feed RSS aggiunto con successo');
      setNewFeed({ name: '', url: '', category: 'generale', enabled: true });
      fetchSettings();
    } catch (err) {
      alert(err.message);
    }
  };

  const deleteFeed = async (index) => {
    if (!window.confirm('Eliminare questo feed?')) return;

    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/settings/rss-feeds/${index}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Errore eliminazione feed');

      alert('Feed eliminato');
      fetchSettings();
    } catch (err) {
      alert(err.message);
    }
  };

  const saveCompanyDomain = async () => {
    if (!companyDomain) {
      alert('Dominio obbligatorio');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_URL}/api/settings/company-domain?domain=${companyDomain}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error('Errore salvataggio');

      alert('Dominio aziendale salvato con successo');
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <p>Caricamento impostazioni...</p>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <SettingsIcon className="inline mr-2" size={32} />
            Impostazioni
          </h1>
          <p className="page-description">
            Configura SMTP, backup e feed news
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-border">
        <div className="flex gap-1">
          <button
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === 'smtp'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('smtp')}
            data-testid="tab-smtp"
          >
            <Mail size={18} className="inline mr-2" />
            Email SMTP
          </button>
          <button
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === 'backup'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('backup')}
            data-testid="tab-backup"
          >
            <Cloud size={18} className="inline mr-2" />
            Backup & Cloud
          </button>
          <button
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === 'rss'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('rss')}
            data-testid="tab-rss"
          >
            <Rss size={18} className="inline mr-2" />
            Feed RSS
          </button>
          <button
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === 'general'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('general')}
            data-testid="tab-general"
          >
            <Globe size={18} className="inline mr-2" />
            Generali
          </button>
          <button
            className={`px-4 py-3 font-medium transition-colors ${
              activeTab === 'weather'
                ? 'text-primary border-b-2 border-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('weather')}
            data-testid="tab-weather"
          >
            🌤️ Meteo
          </button>
        </div>
      </div>

      {/* SMTP Tab */}
      {activeTab === 'smtp' && (
        <div className="card max-w-2xl">
          <h3 className="text-lg font-semibold mb-4">Configurazione SMTP</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Configura il server email per l'invio delle notifiche automatiche
          </p>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Host SMTP</label>
                <input
                  type="text"
                  className="form-input"
                  value={smtp.host}
                  onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  data-testid="smtp-host"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Porta</label>
                <input
                  type="number"
                  className="form-input"
                  value={smtp.port}
                  onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) })}
                  data-testid="smtp-port"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Username / Email Mittente</label>
              <input
                type="text"
                className="form-input"
                value={smtp.username}
                onChange={(e) => setSmtp({ ...smtp, username: e.target.value })}
                placeholder="noreply@azienda.it"
                data-testid="smtp-username"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-input"
                value={smtp.password}
                onChange={(e) => setSmtp({ ...smtp, password: e.target.value })}
                placeholder="••••••••"
                data-testid="smtp-password"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lascia vuoto per mantenere quella esistente
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Email Mittente (From)</label>
              <input
                type="email"
                className="form-input"
                value={smtp.from_email}
                onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })}
                placeholder="noreply@azienda.it"
                data-testid="smtp-from-email"
              />
            </div>

            <div className="form-group">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={smtp.use_tls}
                  onChange={(e) => setSmtp({ ...smtp, use_tls: e.target.checked })}
                  data-testid="smtp-use-tls"
                />
                <span>Usa TLS/SSL</span>
              </label>
            </div>

            <button
              className="btn btn-primary"
              onClick={saveSmtpSettings}
              disabled={saving}
              data-testid="save-smtp"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Salvataggio...' : 'Salva Impostazioni SMTP'}
            </button>
          </div>
        </div>
      )}

      {/* Backup & Cloud Tab */}
      {activeTab === 'backup' && (
        <div className="card max-w-2xl">
          <h3 className="text-lg font-semibold mb-4">Backup & Cloud Storage</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Configura dove salvare i backup automatici
          </p>

          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Provider Cloud</label>
              <select
                className="form-input"
                value={cloudStorage.provider}
                onChange={(e) => setCloudStorage({ ...cloudStorage, provider: e.target.value })}
                data-testid="cloud-provider"
              >
                <option value="s3">Amazon S3</option>
                <option value="gcs">Google Cloud Storage</option>
                <option value="azure">Azure Blob Storage</option>
              </select>
            </div>

            {cloudStorage.provider === 's3' && (
              <>
                <div className="form-group">
                  <label className="form-label">Bucket Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={cloudStorage.bucket_name}
                    onChange={(e) => setCloudStorage({ ...cloudStorage, bucket_name: e.target.value })}
                    placeholder="my-backup-bucket"
                    data-testid="bucket-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Access Key ID</label>
                    <input
                      type="text"
                      className="form-input"
                      value={cloudStorage.access_key}
                      onChange={(e) => setCloudStorage({ ...cloudStorage, access_key: e.target.value })}
                      placeholder="AKIA..."
                      data-testid="access-key"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Secret Access Key</label>
                    <input
                      type="password"
                      className="form-input"
                      value={cloudStorage.secret_key}
                      onChange={(e) => setCloudStorage({ ...cloudStorage, secret_key: e.target.value })}
                      placeholder="••••••••"
                      data-testid="secret-key"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Region</label>
                  <input
                    type="text"
                    className="form-input"
                    value={cloudStorage.region}
                    onChange={(e) => setCloudStorage({ ...cloudStorage, region: e.target.value })}
                    placeholder="us-east-1"
                    data-testid="region"
                  />
                </div>
              </>
            )}

            {cloudStorage.provider === 'gcs' && (
              <div className="form-group">
                <label className="form-label">Bucket Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={cloudStorage.bucket_name}
                  onChange={(e) => setCloudStorage({ ...cloudStorage, bucket_name: e.target.value })}
                  placeholder="my-backup-bucket"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Nota: Per GCS, carica il file JSON delle credenziali sul server
                </p>
              </div>
            )}

            {cloudStorage.provider === 'azure' && (
              <div className="form-group">
                <label className="form-label">Connection String</label>
                <input
                  type="password"
                  className="form-input"
                  value={cloudStorage.connection_string}
                  onChange={(e) => setCloudStorage({ ...cloudStorage, connection_string: e.target.value })}
                  placeholder="DefaultEndpointsProtocol=https..."
                />
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={saveCloudStorageSettings}
              disabled={saving}
              data-testid="save-cloud-storage"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Salvataggio...' : 'Salva Impostazioni Cloud'}
            </button>
          </div>
        </div>
      )}

      {/* RSS Feeds Tab */}
      {activeTab === 'rss' && (
        <div className="card max-w-3xl">
          <h3 className="text-lg font-semibold mb-4">Feed RSS - Testate Giornalistiche</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Aggiungi feed RSS da testate giornalistiche e di settore
          </p>

          {/* Add New Feed */}
          <div className="border border-border rounded-lg p-4 mb-6">
            <h4 className="font-semibold mb-3">Aggiungi Nuovo Feed</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div className="form-group">
                <label className="form-label text-sm">Nome</label>
                <input
                  type="text"
                  className="form-input"
                  value={newFeed.name}
                  onChange={(e) => setNewFeed({ ...newFeed, name: e.target.value })}
                  placeholder="Es: ANSA, Il Sole 24 Ore"
                  data-testid="new-feed-name"
                />
              </div>

              <div className="form-group">
                <label className="form-label text-sm">Categoria</label>
                <select
                  className="form-input"
                  value={newFeed.category}
                  onChange={(e) => setNewFeed({ ...newFeed, category: e.target.value })}
                  data-testid="new-feed-category"
                >
                  <option value="generale">Generale</option>
                  <option value="hse">HSE & Sicurezza</option>
                  <option value="economia">Economia</option>
                  <option value="tecnologia">Tecnologia</option>
                  <option value="normative">Normative</option>
                </select>
              </div>

              <div className="form-group md:col-span-2">
                <label className="form-label text-sm">URL Feed RSS</label>
                <input
                  type="url"
                  className="form-input"
                  value={newFeed.url}
                  onChange={(e) => setNewFeed({ ...newFeed, url: e.target.value })}
                  placeholder="https://www.ansa.it/sito/notizie/rss/rss.xml"
                  data-testid="new-feed-url"
                />
              </div>
            </div>

            <button
              className="btn btn-primary btn-sm"
              onClick={addRssFeed}
              data-testid="add-feed-button"
            >
              <Plus size={16} className="mr-1" />
              Aggiungi Feed
            </button>
          </div>

          {/* Existing Feeds List */}
          <div className="space-y-3">
            <h4 className="font-semibold">Feed Configurati</h4>
            {rssFeeds.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessun feed configurato</p>
            ) : (
              rssFeeds.map((feed, index) => (
                <div
                  key={index}
                  className="border border-border rounded-lg p-3 flex items-center justify-between"
                  data-testid={`feed-${index}`}
                >
                  <div className="flex-1">
                    <p className="font-medium">{feed.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{feed.url}</p>
                    <span className="badge badge-secondary text-xs mt-1">{feed.category}</span>
                  </div>
                  <button
                    className="text-destructive hover:text-destructive/80 ml-4"
                    onClick={() => deleteFeed(index)}
                    data-testid={`delete-feed-${index}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* General Tab */}
      {activeTab === 'general' && (
        <div className="card max-w-2xl">
          <h3 className="text-lg font-semibold mb-4">Impostazioni Generali</h3>

          <div className="space-y-6">
            {/* Company Domain */}
            <div>
              <h4 className="font-semibold mb-3">Dominio Email Aziendale</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Solo email con questo dominio potranno registrarsi
              </p>
              <div className="flex gap-3">
                <div className="form-group flex-1">
                  <input
                    type="text"
                    className="form-input"
                    value={companyDomain}
                    onChange={(e) => setCompanyDomain(e.target.value)}
                    placeholder="azienda.it"
                    data-testid="company-domain"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Esempio: se inserisci "mainsolution.it", solo email @mainsolution.it potranno registrarsi
                  </p>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={saveCompanyDomain}
                  disabled={saving}
                  data-testid="save-domain"
                >
                  <Save size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Weather Tab */}
      {activeTab === 'weather' && (
        <div className="card max-w-2xl">
          <h3 className="text-lg font-semibold mb-4">Configurazione Meteo</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Configura OpenWeatherMap per il widget meteo nella bacheca
          </p>

          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Località</label>
              <input
                type="text"
                className="form-input"
                value={weather.location}
                onChange={(e) => setWeather({ ...weather, location: e.target.value })}
                placeholder="Catanzaro"
                data-testid="weather-location"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Nome città per il meteo (es: Catanzaro, Milano, Roma)
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">API Key OpenWeatherMap</label>
              <input
                type="password"
                className="form-input"
                value={weather.api_key}
                onChange={(e) => setWeather({ ...weather, api_key: e.target.value })}
                placeholder="••••••••••••••••"
                data-testid="weather-api-key"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Ottieni gratuitamente su{' '}
                <a
                  href="https://openweathermap.org/api"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  openweathermap.org/api
                </a>
              </p>
            </div>

            <button
              className="btn btn-primary"
              onClick={async () => {
                setSaving(true);
                const token = localStorage.getItem('token');
                
                try {
                  const res = await fetch(
                    `${API_URL}/api/settings/weather?api_key=${encodeURIComponent(weather.api_key)}&location=${encodeURIComponent(weather.location)}`,
                    {
                      method: 'PUT',
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    }
                  );
                  
                  if (!res.ok) throw new Error('Errore salvataggio');
                  
                  alert('Impostazioni meteo salvate con successo');
                } catch (err) {
                  alert(err.message);
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              data-testid="save-weather"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Salvataggio...' : 'Salva Impostazioni Meteo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
